/**
 * TransactionModal — dodawanie / edycja transakcji.
 *
 * Tryby (type): 'expense' | 'income' | 'transfer'.
 * Header pattern (FarmaPro): 64×64 ikona w ring-8 colored.
 * Layout: kwota → portfel → (kategoria | toWallet) → notatka → data.
 *
 * onSave dispatch do Firebase via api.ts (lub dev store). Domknięcie po success +
 * toast. Walidacja: amount > 0, walletId obowiązkowy, transfer wymaga toWalletId,
 * income/expense wymaga categoryId.
 */
import { useState, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { Icons } from '../Icons';
import { CategoryPicker } from './CategoryPicker';
import { WalletPicker } from './WalletPicker';
import { useWalletsStore } from '../../features/wallets/walletsStore';
import { useCategoriesStore } from '../../features/categories/categoriesStore';
import { useAuthStore } from '../../features/auth/authStore';
import {
  createTransaction,
  updateTransactionRemote,
  deleteTransaction,
  type NewTransactionInput,
} from '../../features/transactions/api';
import type { Transaction, TxType } from '../../features/transactions/transactionsStore';
import { parseAmountInput, formatAmountForInput, haptic } from '../../lib/utils';
import { showToast } from '../ui/Toast';

interface Props {
  type: TxType;
  edit?: Transaction | null;
  onClose: () => void;
}

const META: Record<TxType, { title: string; ring: string; bg: string; icon: typeof Icons.TrendingDown; status: 'danger' | 'success' | 'info' }> = {
  expense: { title: 'Nowy wydatek', ring: 'ring-rose-500/20 dark:ring-rose-400/30', bg: 'bg-rose-100 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400', icon: Icons.TrendingDown, status: 'danger' },
  income: { title: 'Nowy przychód', ring: 'ring-emerald-500/20 dark:ring-emerald-400/30', bg: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', icon: Icons.TrendingUp, status: 'success' },
  transfer: { title: 'Transfer', ring: 'ring-sky-500/20 dark:ring-sky-400/30', bg: 'bg-sky-100 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400', icon: Icons.Transfer, status: 'info' },
};

function toDateInput(d: Date): string {
  // YYYY-MM-DD lokalny czas
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fromDateInput(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  // zachowaj porę aktualnego dnia jeśli today, inaczej południe
  const today = new Date();
  if (dt.toDateString() === today.toDateString()) return today;
  dt.setHours(12, 0, 0, 0);
  return dt;
}

export function TransactionModal({ type: initialType, edit, onClose }: Props) {
  const wallets = useWalletsStore((s) => s.wallets);
  const selectedWalletId = useWalletsStore((s) => s.selectedWalletId);
  const categories = useCategoriesStore((s) => s.categories);
  const user = useAuthStore((s) => s.user);
  const family = useAuthStore((s) => s.family);

  const isEdit = !!edit;
  const [type] = useState<TxType>(edit?.type ?? initialType);
  const meta = META[type];

  // Defaultowy portfel: edycja → tx.walletId, inaczej selected (jeśli nie shared) lub pierwszy personal
  const personalWallets = useMemo(() => wallets.filter((w) => w.type !== 'shared'), [wallets]);
  const defaultWallet = useMemo(() => {
    if (edit) return edit.walletId;
    const sel = wallets.find((w) => w.id === selectedWalletId);
    if (sel && sel.type !== 'shared') return sel.id;
    return personalWallets[0]?.id ?? wallets[0]?.id ?? '';
  }, [edit, wallets, selectedWalletId, personalWallets]);

  const [amount, setAmount] = useState(edit ? formatAmountForInput(edit.amount) : '');
  const [walletId, setWalletId] = useState(defaultWallet);
  const [toWalletId, setToWalletId] = useState(edit?.toWalletId ?? personalWallets.find((w) => w.id !== defaultWallet)?.id ?? '');
  const [categoryId, setCategoryId] = useState<string | null>(edit?.categoryId ?? null);
  const [note, setNote] = useState(edit?.note ?? '');
  const [date, setDate] = useState<Date>(edit?.date ?? new Date());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const Icon = meta.icon;

  const validate = (): string | null => {
    const grosze = parseAmountInput(amount);
    if (!grosze || grosze <= 0) return 'Podaj kwotę większą od zera';
    if (!walletId) return 'Wybierz portfel';
    if (type === 'transfer') {
      if (!toWalletId) return 'Wybierz portfel docelowy';
      if (toWalletId === walletId) return 'Portfele muszą się różnić';
    } else {
      if (!categoryId) return 'Wybierz kategorię';
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      haptic('error');
      return;
    }
    if (!user || !family) {
      setError('Brak sesji — zaloguj się ponownie');
      return;
    }
    setError(null);
    setSubmitting(true);
    const grosze = parseAmountInput(amount)!;
    const payload: NewTransactionInput = {
      familyId: family.id,
      userId: user.uid,
      type,
      amount: grosze,
      walletId,
      toWalletId: type === 'transfer' ? toWalletId : undefined,
      categoryId: type === 'transfer' ? null : categoryId,
      note: note.trim(),
      date,
    };
    try {
      if (isEdit && edit) {
        await updateTransactionRemote(edit.id, payload);
        showToast('Zaktualizowano transakcję', 'success');
      } else {
        await createTransaction(payload);
        showToast(type === 'expense' ? 'Wydatek dodany' : type === 'income' ? 'Przychód dodany' : 'Transfer wykonany', 'success');
      }
      haptic('success');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się zapisać');
      haptic('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!edit) return;
    if (!window.confirm('Usunąć tę transakcję?')) return;
    setSubmitting(true);
    try {
      await deleteTransaction(edit.id);
      showToast('Transakcja usunięta', 'success');
      haptic('success');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się usunąć');
      haptic('error');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size="md"
      status={meta.status}
      ariaLabel={isEdit ? `Edycja: ${meta.title}` : meta.title}
      icon={
        <div className={`w-16 h-16 rounded-full ${meta.bg} ring-8 ${meta.ring} flex items-center justify-center`}>
          <Icon size={28} strokeWidth={2.25} />
        </div>
      }
      title={isEdit ? 'Edycja transakcji' : meta.title}
      subtitle={isEdit ? 'Zmień szczegóły lub usuń' : undefined}
      footer={
        <div className="flex gap-2">
          {isEdit && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="px-4 py-3 rounded-xl border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-500/10 transition disabled:opacity-50"
            >
              <Icons.Trash size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-[1.5] px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card disabled:opacity-50 transition"
          >
            {submitting ? 'Zapisuję...' : isEdit ? 'Zapisz zmiany' : 'Dodaj'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Kwota */}
        <div>
          <label htmlFor="tx-amount" className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Kwota
          </label>
          <div className="relative">
            <input
              id="tx-amount"
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="w-full text-3xl font-bold tabular-nums text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800/50 rounded-xl px-4 py-4 pr-14 border border-slate-200 dark:border-slate-700 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition"
              autoComplete="off"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-base font-semibold text-slate-400">zł</span>
          </div>
        </div>

        {/* Portfel źródłowy */}
        <WalletPicker
          wallets={wallets}
          value={walletId}
          onChange={setWalletId}
          excludeShared
          label={type === 'transfer' ? 'Z portfela' : 'Portfel'}
        />

        {/* Transfer: portfel docelowy. Income/expense: kategoria. */}
        {type === 'transfer' ? (
          <WalletPicker
            wallets={wallets}
            value={toWalletId}
            onChange={setToWalletId}
            excludeShared
            excludeId={walletId}
            label="Do portfela"
          />
        ) : (
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
              Kategoria
            </p>
            <CategoryPicker
              categories={categories}
              type={type}
              value={categoryId}
              onChange={setCategoryId}
            />
          </div>
        )}

        {/* Notatka */}
        <div>
          <label htmlFor="tx-note" className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Notatka <span className="text-slate-400 normal-case font-normal">(opcjonalna)</span>
          </label>
          <input
            id="tx-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={type === 'expense' ? 'np. Biedronka' : type === 'income' ? 'np. Wynagrodzenie' : 'np. Wpłata na wakacje'}
            maxLength={120}
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition text-sm"
          />
        </div>

        {/* Data */}
        <div>
          <label htmlFor="tx-date" className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Data
          </label>
          <input
            id="tx-date"
            type="date"
            value={toDateInput(date)}
            onChange={(e) => setDate(fromDateInput(e.target.value))}
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition text-sm"
          />
        </div>

        {error && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-sm text-rose-700 dark:text-rose-300">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
